import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import {
  Box,
  Typography,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useTheme,
  ButtonBase,
  useMediaQuery,
} from "@mui/material";

// helper to format time mm:ss
const fmt = (t) => new Date(t * 1000).toISOString().substr(14, 5);

const tagChipSx = {
  borderRadius: 999,
  px: 1.4,
  height: 24,
  fontSize: "0.7rem",
  textTransform: "none",
  borderColor: "rgba(255,255,255,0.35)",
  bgcolor: "rgba(255,255,255,0.06)",
};

const CabinetDialog = ({
  open,
  cabinet,
  onClose,
  onPrevCabinet,
  onNextCabinet,
  canCycle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedMicId, setSelectedMicId] = React.useState(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [time, setTime] = React.useState(0);
  const [isLooping, setIsLooping] = React.useState(true);
  const [isScrubbing, setIsScrubbing] = React.useState(false);

  // refs for all mic audio elements (hidden)
  const audioRefs = React.useRef([]);
  const getAudioRef = (i) => (el) => {
    audioRefs.current[i] = el;
  };

  // Derive mics + selected mic even when cabinet is null
  const mics = cabinet?.mics || [];
  const selectedMicIndexRaw = mics.findIndex((m) => m.id === selectedMicId);
  const selectedMicIndex = selectedMicIndexRaw === -1 ? 0 : selectedMicIndexRaw;
  const selectedMic = mics[selectedMicIndex] || mics[0] || null;
  const micCount = mics.length || 1;
  const segmentWidthDesktop = 100 / micCount;

  // ---------- MOBILE MIC WINDOW / NAV LOGIC ----------
  const [micStartIndex, setMicStartIndex] = React.useState(0);

  const visibleMicCount = isMobile
    ? Math.min(3, mics.length || 0)
    : mics.length || 0;

  const showMicNav = isMobile && mics.length > visibleMicCount;

  // Reset window when dialog opens or layout mode changes
  React.useEffect(() => {
    if (!open) return;
    setMicStartIndex(0);
  }, [open, isMobile]);

  React.useEffect(() => {
    if (!showMicNav) {
      setMicStartIndex(0);
      return;
    }
    if (!mics.length) return;

    setMicStartIndex((prev) => {
      if (selectedMicIndex < prev) return selectedMicIndex;
      if (selectedMicIndex >= prev + visibleMicCount) {
        return selectedMicIndex - visibleMicCount + 1;
      }
      return prev;
    });
  }, [selectedMicIndex, showMicNav, visibleMicCount, mics.length]);

  const visibleMics = React.useMemo(() => {
    if (!mics.length) return [];
    if (!showMicNav) return mics;
    return mics.slice(micStartIndex, micStartIndex + visibleMicCount);
  }, [mics, showMicNav, micStartIndex, visibleMicCount]);

  const visibleIndex = showMicNav
    ? selectedMicIndex - micStartIndex
    : selectedMicIndex;

  const mobileSegmentCount = visibleMics.length || 1;
  const mobileSegmentWidth = 100 / mobileSegmentCount;
  const mobileHighlightLeft =
    visibleIndex >= 0 && visibleIndex < mobileSegmentCount
      ? visibleIndex * mobileSegmentWidth
      : 0;

  // NOW based on selected mic, not the window
  const canNavLeft = showMicNav && selectedMicIndex > 0;
  const canNavRight = showMicNav && selectedMicIndex < mics.length - 1;

  // nav buttons now select previous/next mic (and window follows via effect)
  const handleMicNav = (dir) => {
    if (!mics.length) return;
    const curr = selectedMicIndex;
    if (dir === "left" && curr > 0) {
      onMicButtonClick(curr - 1);
    } else if (dir === "right" && curr < mics.length - 1) {
      onMicButtonClick(curr + 1);
    }
  };

  // ---------- OPEN/CLOSE LIFECYCLE ---------------------------------

  React.useEffect(() => {
    if (open && cabinet && cabinet.mics?.length) {
      // keep the same mic id if this cabinet also has it
      setSelectedMicId((prev) => {
        if (prev && cabinet.mics.some((m) => m.id === prev)) return prev;
        return cabinet.mics[0].id;
      });
    }

    // Reset when dialog closes
    if (!open) {
      setSelectedMicId(null);
      setIsPlaying(false);
      setTime(0);
      setDuration(0);
      if (audioRefs.current.length) {
        audioRefs.current.forEach((a) => {
          if (!a) return;
          a.pause();
          a.currentTime = 0;
        });
      }
    }
  }, [open, cabinet]);

  // Reset duration when cabinet changes (but keep time)
  React.useEffect(() => {
    if (!cabinet) return;
    setDuration(0);
  }, [cabinet]);

  // ---------- CABINET CHANGE: behave like mic switch ----------------

  React.useEffect(() => {
    if (!open || !cabinet || !cabinet.mics?.length) return;

    // pick mic id: keep same id if present, otherwise first mic
    setSelectedMicId((prev) => {
      if (prev && cabinet.mics.some((m) => m.id === prev)) return prev;
      return cabinet.mics[0].id;
    });

    // align new cabinet's tracks to current time
    const t = time;
    audioRefs.current.forEach((a) => {
      if (!a) return;
      try {
        a.currentTime = t; // explicit position change on cabinet switch
      } catch {
        /* ignore */
      }
    });

    // if we were playing before, keep playing on new cabinet
    if (isPlaying) {
      audioRefs.current.forEach((a, i) => {
        if (!a) return;
        const isActive = i === selectedMicIndex;
        a.loop = isLooping;
        a.playsInline = true;
        a.muted = !isActive;
        a.volume = isActive ? 1 : 0;
      });

      Promise.all(
        audioRefs.current.map(async (a) => {
          if (!a) return;
          if (a.paused) {
            try {
              await a.play();
            } catch {
              // Safari may block; nothing we can do without user gesture
            }
          }
        })
      ).then(() => {
        setIsPlaying(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinet, open]);

  // ---------- AUDIO METADATA & AUDIBILITY ---------------------------

  // Collect duration from all tracks (take max)
  const handleLoadedMeta = (e) => {
    const d = e.currentTarget.duration;
    if (!Number.isNaN(d) && d !== Infinity) {
      setDuration((prev) => Math.max(prev, d));
    }
  };

  // Keep only the selected mic audible
  React.useEffect(() => {
    audioRefs.current.forEach((a, i) => {
      if (!a) return;
      const isActive = i === selectedMicIndex;
      a.muted = !isActive;
      a.volume = isActive ? 1 : 0;
    });
  }, [selectedMicIndex, mics.length]);

  // ---------- PLAYBACK CONTROL (Safari-friendly, no auto reseek) ----

  const playAll = async () => {
    audioRefs.current.forEach((a, i) => {
      if (!a) return;
      const isActive = i === selectedMicIndex;
      a.loop = isLooping;
      a.playsInline = true;
      a.muted = !isActive;
      a.volume = isActive ? 1 : 0;
    });

    try {
      await Promise.all(
        audioRefs.current.map(async (a) => {
          if (!a) return;
          if (a.paused) await a.play();
        })
      );
      setIsPlaying(true);
    } catch {
      // Safari might block autoplay without gesture
    }
  };

  const pauseAll = () => {
    audioRefs.current.forEach((a) => a && a.pause());
    setIsPlaying(false);
  };

  const restartAll = () => {
    const wasPlaying = isPlaying;
    pauseAll();
    audioRefs.current.forEach((a) => {
      if (a) a.currentTime = 0; // explicit position change
    });
    setTime(0);
    if (wasPlaying) playAll();
  };

  const commitSeek = (t) => {
    const wasPlaying = isPlaying;
    pauseAll();
    audioRefs.current.forEach((a) => {
      if (a) a.currentTime = t; // explicit position change
    });
    setTime(t);
    if (wasPlaying) playAll();
  };

  const togglePlay = () => {
    if (isPlaying) pauseAll();
    else playAll(); // no seeking here
  };

  const toggleLoop = () => {
    const nextLoop = !isLooping;
    setIsLooping(nextLoop);
    audioRefs.current.forEach((a) => {
      if (a) a.loop = nextLoop;
    });
  };

  // Mic switch: align all tracks to this mic’s currentTime
  const onMicButtonClick = (idxGlobal) => {
    const targetMic = mics[idxGlobal];
    if (!targetMic) return;

    const master = audioRefs.current[idxGlobal];
    const newTime = master?.currentTime || time;

    audioRefs.current.forEach((a) => {
      if (!a) return;
      try {
        a.currentTime = newTime; // explicit position change: mic switch
      } catch {
        /* ignore */
      }
    });

    setTime(newTime);
    setSelectedMicId(targetMic.id);
  };

  // UI time: just read from current mic, never reseek
  React.useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      const master = audioRefs.current[selectedMicIndex];
      if (!master) return;
      setTime(master.currentTime || 0);
    }, 200);
    return () => clearInterval(id);
  }, [isPlaying, selectedMicIndex]);

  // All hooks above this line
  if (!cabinet) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: "rgba(24, 24, 28, 0.98)",
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow:
            "0 22px 60px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.08)",
          maxHeight: "80vh",
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          pr: 6,
          py: 1.2,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {cabinet.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              opacity: 0.75,
              display: "block",
              mt: 0.3,
            }}
          >
            {cabinet.subtitle}
          </Typography>
        </Box>

        <IconButton aria-label="close" onClick={onClose} sx={{ ml: "auto" }}>
          <CloseIcon sx={{ color: "white" }} fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          py: 2.5,
          flex: 1,
          overflowY: "auto",
        }}
      >
        {/* Centered content column */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {/* Cab art + big arrows overlaid */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid",
              borderColor: theme.palette.divider,
              mb: 1.5,
              position: "relative",
              flexShrink: 0,
            }}
          >
            {/* Left arrow */}
            <IconButton
              aria-label="previous cabinet"
              onClick={canCycle ? onPrevCabinet : undefined}
              disabled={!canCycle}
              sx={{
                position: "absolute",
                left: 6,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                bgcolor: "rgba(0,0,0,0.6)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.9)" },
                borderRadius: "999px",
                p: 0.5,
              }}
            >
              <NavigateBeforeIcon
                sx={{
                  fontSize: 32,
                  color: canCycle ? "white" : "rgba(255,255,255,0.35)",
                }}
              />
            </IconButton>

            {/* Right arrow */}
            <IconButton
              aria-label="next cabinet"
              onClick={canCycle ? onNextCabinet : undefined}
              disabled={!canCycle}
              sx={{
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                bgcolor: "rgba(0,0,0,0.6)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.9)" },
                borderRadius: "999px",
                p: 0.5,
              }}
            >
              <NavigateNextIcon
                sx={{
                  fontSize: 32,
                  color: canCycle ? "white" : "rgba(255,255,255,0.35)",
                }}
              />
            </IconButton>

            <Box
              component="img"
              src={cabinet.image}
              alt={cabinet.name}
              sx={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          {/* Tag chips – pill-shaped bubbles */}
          {cabinet.tags && cabinet.tags.length > 0 && (
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              justifyContent="center"
              sx={{ mb: 1.5 }}
            >
              {cabinet.tags.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  label={tag}
                  variant="outlined"
                  sx={tagChipSx}
                />
              ))}
            </Stack>
          )}

          {/* Amp label under cab image */}
          {selectedMic && selectedMic.amp && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                opacity: 0.8,
                mb: 1.5,
              }}
            >
              Amp used for these demos: {selectedMic.amp}
            </Typography>
          )}

          {/* Intro text */}
          <Typography
            variant="body1"
            sx={{
              opacity: 0.85,
              color: theme.palette.text.secondary,
              lineHeight: 1.5,
              maxWidth: 800,
              mb: 2.5,
            }}
          >
            Choose a microphone below to hear how it shapes this cabinet. Each
            mic has its own position and distance from the speaker, giving you
            different flavors of the same cab.
          </Typography>

          {/* Mic selection – DESKTOP: original pill bar; MOBILE: windowed with nav that selects next/prev */}
          {mics.length > 0 && (
            <>
              {isMobile ? (
                // -------- MOBILE VERSION ----------
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 480,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2.5,
                    gap: 1,
                  }}
                >
                  {/* Left nav (now selects prev mic) */}
                  {showMicNav && (
                    <IconButton
                      size="small"
                      onClick={() => handleMicNav("left")}
                      disabled={!canNavLeft}
                      sx={{
                        bgcolor: "rgba(0,0,0,0.7)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.95)" },
                        borderRadius: "999px",
                        p: 0.5,
                        visibility: showMicNav ? "visible" : "hidden",
                      }}
                    >
                      <NavigateBeforeIcon
                        sx={{
                          fontSize: 20,
                          color: canNavLeft
                            ? "white"
                            : "rgba(255,255,255,0.4)",
                        }}
                      />
                    </IconButton>
                  )}

                  <Box
                    sx={{
                      position: "relative",
                      flex: 1,
                      borderRadius: 999,
                      padding: 0.25,
                      bgcolor: "rgba(0,0,0,0.7)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.8)",
                      overflow: "hidden",
                      display: "flex",
                    }}
                  >
                    {/* Sliding highlight bubble – per visible mic segment */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 3,
                        bottom: 3,
                        left: `${mobileHighlightLeft}%`,
                        width: `${mobileSegmentWidth}%`,
                        borderRadius: 999,
                        bgcolor: "rgba(255,255,255,0.16)",
                        boxShadow: "0 0 0 1px rgba(255,255,255,0.4)",
                        transition:
                          "left 0.22s ease-out, width 0.22s ease-out, background-color 0.22s ease-out",
                        pointerEvents: "none",
                      }}
                    />

                    {visibleMics.map((mic, visibleIdx) => {
                      const globalIndex = showMicNav
                        ? micStartIndex + visibleIdx
                        : visibleIdx;
                      const isSelected = globalIndex === selectedMicIndex;
                      const isLast = visibleIdx === visibleMics.length - 1;
                      return (
                        <ButtonBase
                          key={mic.id}
                          onClick={() => onMicButtonClick(globalIndex)}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            px: 1.1,
                            py: 0.65,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.35,
                            borderRadius: 999,
                            transition: "all 0.18s ease-out",
                            position: "relative",
                            zIndex: 1,
                            borderRight: isLast
                              ? "none"
                              : "1px solid rgba(0,0,0,0.16)",
                            bgcolor: "transparent",
                            "&:hover": {
                              transform: "none",
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src={mic.icon}
                            alt={mic.name}
                            sx={{
                              display: "block",
                              width: 24,
                              height: 24,
                              objectFit: "contain",
                              opacity: isSelected ? 1 : 0.8,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              lineHeight: 1.2,
                              textAlign: "center",
                              opacity: isSelected ? 1 : 0.8,
                            }}
                          >
                            {mic.name}
                          </Typography>
                        </ButtonBase>
                      );
                    })}
                  </Box>

                  {/* Right nav (now selects next mic) */}
                  {showMicNav && (
                    <IconButton
                      size="small"
                      onClick={() => handleMicNav("right")}
                      disabled={!canNavRight}
                      sx={{
                        bgcolor: "rgba(0,0,0,0.7)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.95)" },
                        borderRadius: "999px",
                        p: 0.5,
                        visibility: showMicNav ? "visible" : "hidden",
                      }}
                    >
                      <NavigateNextIcon
                        sx={{
                          fontSize: 20,
                          color: canNavRight
                            ? "white"
                            : "rgba(255,255,255,0.4)",
                        }}
                      />
                    </IconButton>
                  )}
                </Box>
              ) : (
                // -------- DESKTOP VERSION ----------
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    mb: 2.5,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      display: "inline-flex",
                      borderRadius: 999,
                      padding: 0.25,
                      bgcolor: "rgba(0,0,0,0.7)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.8)",
                      overflow: "hidden",
                    }}
                  >
                    {/* Sliding highlight bubble – white-based */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 3,
                        bottom: 3,
                        left: `${segmentWidthDesktop * selectedMicIndex}%`,
                        width: `${segmentWidthDesktop}%`,
                        borderRadius: 999,
                        bgcolor: "rgba(255,255,255,0.16)",
                        boxShadow: "0 0 0 1px rgba(255,255,255,0.4)",
                        transition:
                          "left 0.22s ease-out, width 0.22s ease-out, background-color 0.22s ease-out",
                        pointerEvents: "none",
                      }}
                    />

                    {mics.map((mic, index) => {
                      const isSelected = index === selectedMicIndex;
                      const isLast = index === mics.length - 1;
                      return (
                        <ButtonBase
                          key={mic.id}
                          onClick={() => onMicButtonClick(index)}
                          sx={{
                            px: 1.3,
                            py: 0.75,
                            minWidth: 100,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.35,
                            borderRadius: 999,
                            transition: "all 0.18s ease-out",
                            position: "relative",
                            zIndex: 1,
                            borderRight: isLast
                              ? "none"
                              : "1px solid rgba(0,0,0,0.16)",
                            bgcolor: "transparent",
                            "&:hover": {
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src={mic.icon}
                            alt={mic.name}
                            sx={{
                              display: "block",
                              width: 30,
                              height: 30,
                              objectFit: "contain",
                              opacity: isSelected ? 1 : 0.8,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.7rem",
                              lineHeight: 1.2,
                              textAlign: "center",
                              opacity: isSelected ? 1 : 0.8,
                            }}
                          >
                            {mic.name}
                          </Typography>
                        </ButtonBase>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Mic details – also centered */}
          {selectedMic && (
            <Box sx={{ maxWidth: 640, width: "100%" }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, fontSize: "0.85rem", mb: 0.25 }}
              >
                {selectedMic.name}
              </Typography>

              {/* Real mic counterpart line */}
              {selectedMic.realMic && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    opacity: 0.8,
                    mb: 0.3,
                  }}
                >
                  <i>
                    Captured with its real-world {selectedMic.realMic} counterpart.
                  </i>
                </Typography>
              )}

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  opacity: 0.8,
                  mb: 1,
                }}
              >
                <i>
                  Position: {selectedMic.position} · Distance:{" "}
                  {selectedMic.distance}
                </i>
              </Typography>

              {/* Hidden audio elements (one per mic, no visible list) */}
              {mics.map((mic, idx) => (
                <audio
                  key={mic.id || idx}
                  ref={getAudioRef(idx)}
                  preload="none"
                  src={mic.audio}
                  loop={isLooping}
                  playsInline
                  onLoadedMetadata={handleLoadedMeta}
                  style={{ display: "none" }}
                  aria-hidden
                />
              ))}

              {/* Transport */}
              <Stack spacing={1.5} sx={{ mt: 1, mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={togglePlay}
                    sx={{ textTransform: "none", fontSize: "0.75rem" }}
                  >
                    {isPlaying ? "Pause" : "Play"}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={restartAll}
                    sx={{ textTransform: "none", fontSize: "0.75rem" }}
                  >
                    Restart
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 56, textAlign: "right", ml: "auto" }}
                  >
                    {fmt(time)} / {duration ? fmt(duration) : "--:--"}
                  </Typography>
                </Stack>

                {/* Seek bar */}
                <Box sx={{ px: 0.5 }}>
                  <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    step={0.01}
                    value={isScrubbing ? undefined : time}
                    onChange={(e) => {
                      const t = parseFloat(e.target.value);
                      setIsScrubbing(true);
                      setTime(t); // UI only
                    }}
                    onMouseUp={(e) => {
                      const t = parseFloat(e.currentTarget.value);
                      setIsScrubbing(false);
                      commitSeek(t);
                    }}
                    onTouchEnd={(e) => {
                      const input = e.currentTarget;
                      const t = parseFloat(input.value);
                      setIsScrubbing(false);
                      commitSeek(t);
                    }}
                    onKeyUp={(e) => {
                      if (
                        ["ArrowLeft", "ArrowRight", "Home", "End"].includes(
                          e.key
                        )
                      ) {
                        const t = parseFloat(e.currentTarget.value);
                        setIsScrubbing(false);
                        commitSeek(t);
                      }
                    }}
                    style={{ width: "100%" }}
                    aria-label="Seek"
                  />
                </Box>

                {/* Loop */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant={isLooping ? "contained" : "outlined"}
                    onClick={toggleLoop}
                    sx={{ textTransform: "none", fontSize: "0.75rem" }}
                  >
                    {isLooping ? "Loop: On" : "Loop: Off"}
                  </Button>
                </Stack>
              </Stack>

              {/* Descriptive text BELOW the player */}
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.85,
                  color: theme.palette.text.secondary,
                  lineHeight: 1.5,
                  pb: 1,
                }}
              >
                {selectedMic.description}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1,
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Button onClick={onClose} size="small" sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CabinetDialog;
